import { useCallback, useState } from 'react';
import { send_request } from '../../utils/Request';
import { CREDENTIAL } from './CredentialConstants';

const HTTP_OK = 200;

/**
 * Shared create/save for a tenant credential, extracted so the agent-driven credential setup flow
 * (CredentialSetupModal) and any future caller can persist a credential without depending on the
 * CredentialStorePage class component. v1 targets VARIABLE secrets; the payload shape matches
 * CredentialStorePage.onSave so the backend contract is identical.
 *
 * @returns {{ saveCredential: (input) => Promise<{credentialId: string, placeholder: string}>,
 *            saving: boolean, error: string|null }}
 */
export default function useCredentialSave() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const saveCredential = useCallback(
    async ({
      name,
      description = '',
      placeholder,
      secretValue,
      type = CREDENTIAL.CREDENTIAL_TYPE.VARIABLE,
    }) => {
      setSaving(true);
      setError(null);
      try {
        const data = {
          name: name || placeholder,
          type,
          credentialValue: secretValue,
          data: {
            displayName: name || placeholder,
            placeholder,
          },
          description,
        };
        const res = await send_request('ai-service/credential/user', data, '', 'POST');
        if (!res || res.status !== HTTP_OK || !res.data) {
          throw new Error('Failed to save credential');
        }
        const saved = res.data;
        return {
          credentialId: saved.credentialId,
          placeholder: saved?.data?.placeholder || placeholder,
        };
      } catch (e) {
        const message = e?.message || 'Failed to save credential';
        setError(message);
        throw e;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return { saveCredential, saving, error };
}
